import MemoryIcon from '@mui/icons-material/Memory';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import type { ReactNode } from 'react';

/// How each backend is shown. Apple's mark is a shape, the others are their names set in their
/// own colour: a traced approximation of somebody's logo looks worse than their name does, and
/// this is here to say whose hardware it is, not to decorate the page.
const marks: Record<string, { label: string; colour: string; mark: ReactNode }> = {
  VideoToolbox: {
    label: 'Apple',
    // Apple's own mark is black or white and nothing else, which on a page that is both is
    // exactly the page's own text colour.
    colour: 'text.primary',
    mark: (
      <Box component="svg" viewBox="0 0 24 24" sx={{ width: 22, height: 22, fill: 'currentColor' }}>
        <path d="M16.365 1.43c0 1.14-.467 2.244-1.226 3.043-.784.83-2.063 1.47-3.12 1.386-.128-1.11.44-2.28 1.163-3.02.8-.83 2.17-1.44 3.183-1.41zM20.69 8.36c-.19.116-2.62 1.48-2.59 4.39.03 3.49 3.06 4.65 3.1 4.66-.02.08-.48 1.66-1.6 3.28-.96 1.41-1.97 2.81-3.55 2.84-1.55.03-2.05-.92-3.82-.92-1.77 0-2.33.89-3.8.95-1.53.06-2.69-1.52-3.66-2.92-1.99-2.87-3.5-8.11-1.46-11.65.99-1.75 2.77-2.86 4.7-2.89 1.5-.03 2.91.99 3.82.99.91 0 2.63-1.22 4.43-1.04.75.03 2.87.3 4.23 2.29z" />
      </Box>
    ),
  },
  Nvenc: {
    label: 'NVIDIA',
    colour: '#76B900',
    mark: <Wordmark>NVIDIA</Wordmark>,
  },
  QuickSync: {
    label: 'Intel',
    colour: '#0068B5',
    mark: <Wordmark>intel</Wordmark>,
  },
  // VAAPI says which interface is in use, not whose silicon is behind it - on Linux it is the
  // road to an AMD card as often as to an Intel one. A chip, then, and no claim about the maker.
  Vaapi: {
    label: 'VAAPI',
    colour: '#9BA3B0',
    mark: <MemoryIcon sx={{ fontSize: 22 }} />,
  },
};

function Wordmark({ children }: { children: string }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: children.length > 5 ? 9 : 12,
        fontWeight: 800,
        letterSpacing: '0.02em',
        lineHeight: 1,
        textTransform: children === 'intel' ? 'lowercase' : 'uppercase',
      }}
    >
      {children}
    </Box>
  );
}

/// The badge in front of a device, so a shelf of them can be told apart at a glance rather
/// than read word by word.
export default function HardwareVendorMark({
  accelerator,
  dimmed = false,
}: {
  accelerator: string;
  dimmed?: boolean;
}) {
  const mark = marks[accelerator];

  return (
    <Tooltip title={mark?.label ?? accelerator}>
      <Box
        aria-label={mark?.label ?? accelerator}
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: 2.5,
          display: 'grid',
          placeItems: 'center',
          color: mark?.colour ?? 'text.secondary',
          // The mark's own colour, laid on thinly enough to work on either ground.
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(16,19,25,0.04)',
          border: '1px solid',
          borderColor: 'divider',
          filter: dimmed ? 'grayscale(1)' : undefined,
          opacity: dimmed ? 0.7 : 1,
          transition: 'filter 160ms ease, opacity 160ms ease',
        }}
      >
        {mark?.mark ?? <MemoryIcon sx={{ fontSize: 22 }} />}
      </Box>
    </Tooltip>
  );
}
